import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import { Grid, Typography, FormControlLabel, Checkbox } from '@mui/material';
import StructuredModal from '../../../../../components/structuredModal/structuredModal';
import ControlledSelectField from '../../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../../../../../components/controlledComponents/ControlledTextField.component';
import ControlledDateField from '../../../../../components/controlledComponents/ControlledDateField.component';
import {
  TDriver,
  TViewType,
} from 'src/apis/administration/administration.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import {
  isIsoDateRangeOrdered,
  zDateStringFromInput,
  zNumberFromInput,
  zOptionalDateStringFromInput,
} from 'src/utils/zodFormHelpers';
import {
  useRoutesAndScheduleState,
  useRoutesAndScheduleActions,
} from '../../../contexts/routesAndSchedule.context';

type SalaryFormData = {
  name: string;
  licenseNumber: string;
  licenseType: 'LMV' | 'HMV' | 'PSV';
  licenseExpiryDate: string;
  phoneNumber: string;
  email?: string;
  address: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  joiningDate: string;
  salary: number;
  salaryStatus: 'PAID' | 'PENDING' | 'OVERDUE';
  lastSalaryPaidDate?: string;
  nextSalaryDueDate: string;
  isActive: boolean;
  experience: number;
  medicalCertificateExpiryDate: string;
  policeVerificationStatus: 'PENDING' | 'VERIFIED' | 'REJECTED';
  assignedVehicleId?: string;
  assignedRouteId?: string;
};

const getSalaryFormSchema = (
  translateText: (key: string) => string
): ZodType<SalaryFormData> =>
  z.object({
    name: z.string().min(1, translateText('common.fieldRequired')),
    licenseNumber: z.string().min(1, translateText('common.fieldRequired')),
    licenseType: z.enum(['LMV', 'HMV', 'PSV'], {
      required_error: translateText('common.fieldRequired'),
    }),
    licenseExpiryDate: zDateStringFromInput({
      requiredMessage: translateText('common.fieldRequired') || undefined,
      message: translateText('common.dateInvalid') || undefined,
    }),
    phoneNumber: z.string().min(1, translateText('common.fieldRequired')),
    email: z.string().email().optional().or(z.literal('')),
    address: z.string().min(1, translateText('common.fieldRequired')),
    emergencyContact: z.string().min(1, translateText('common.fieldRequired')),
    emergencyContactPhone: z
      .string()
      .min(1, translateText('common.fieldRequired')),
    joiningDate: zDateStringFromInput({
      requiredMessage: translateText('common.fieldRequired') || undefined,
      message: translateText('common.dateInvalid') || undefined,
    }),
    salary: zNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    salaryStatus: z.enum(['PAID', 'PENDING', 'OVERDUE'], {
      required_error: translateText('common.fieldRequired'),
    }),
    lastSalaryPaidDate: zOptionalDateStringFromInput({
      message: translateText('common.dateInvalid') || undefined,
    }),
    nextSalaryDueDate: zDateStringFromInput({
      requiredMessage: translateText('common.fieldRequired') || undefined,
      message: translateText('common.dateInvalid') || undefined,
    }),
    isActive: z.boolean(),
    experience: zNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    medicalCertificateExpiryDate: zDateStringFromInput({
      requiredMessage: translateText('common.fieldRequired') || undefined,
      message: translateText('common.dateInvalid') || undefined,
    }),
    policeVerificationStatus: z.enum(['PENDING', 'VERIFIED', 'REJECTED'], {
      required_error: translateText('common.fieldRequired'),
    }),
    assignedVehicleId: z.string().optional(),
    assignedRouteId: z.string().optional(),
  })
    .refine(
      (data) =>
        isIsoDateRangeOrdered(
          data.lastSalaryPaidDate,
          data.nextSalaryDueDate
        ),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['nextSalaryDueDate'],
      }
    ) as ZodType<SalaryFormData>;

const getLicenseTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: 'LMV',
      value:
        translateText('administration.routesAndSchedule.licenseTypes.lmv') ||
        'LMV',
    },
    {
      key: 'HMV',
      value:
        translateText('administration.routesAndSchedule.licenseTypes.hmv') ||
        'HMV',
    },
    {
      key: 'PSV',
      value:
        translateText('administration.routesAndSchedule.licenseTypes.psv') ||
        'PSV',
    },
  ];

const getSalaryStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: 'PAID',
      value:
        translateText('administration.routesAndSchedule.status.paid') || 'Paid',
    },
    {
      key: 'PENDING',
      value:
        translateText('administration.routesAndSchedule.status.pending') ||
        'Pending',
    },
    {
      key: 'OVERDUE',
      value:
        translateText('administration.routesAndSchedule.status.overdue') ||
        'Overdue',
    },
  ];

const getPoliceVerificationOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: 'PENDING',
      value:
        translateText('administration.routesAndSchedule.status.pending') ||
        'Pending',
    },
    {
      key: 'VERIFIED',
      value:
        translateText(
          'administration.routesAndSchedule.policeVerification.verified'
        ) || 'Verified',
    },
    {
      key: 'REJECTED',
      value:
        translateText(
          'administration.routesAndSchedule.policeVerification.rejected'
        ) || 'Rejected',
    },
  ];

const AddEditSalaryDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const salaryFormSchema = getSalaryFormSchema(translateText);
  const { selectedDriver, editMode, viewType, vehicles, routes } =
    useRoutesAndScheduleState();
  const { setEditMode, setViewType, addDriver, updateDriver } =
    useRoutesAndScheduleActions();

  const isEditMode = editMode === 'edit' && viewType === TViewType.SALARIES;
  const isCreateMode = editMode === 'create' && viewType === TViewType.SALARIES;
  const isOpen =
    (isEditMode || isCreateMode) && viewType === TViewType.SALARIES;

  const defaultValues: SalaryFormData = {
    name: '',
    licenseNumber: '',
    licenseType: 'LMV',
    licenseExpiryDate: '',
    phoneNumber: '',
    email: '',
    address: '',
    emergencyContact: '',
    emergencyContactPhone: '',
    joiningDate: '',
    salary: 0,
    salaryStatus: 'PENDING',
    lastSalaryPaidDate: '',
    nextSalaryDueDate: '',
    isActive: true,
    experience: 0,
    medicalCertificateExpiryDate: '',
    policeVerificationStatus: 'PENDING',
    assignedVehicleId: '',
    assignedRouteId: '',
  };

  const methods = useForm<SalaryFormData>({
    resolver: zodResolver(salaryFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = methods;

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && selectedDriver) {
        reset({
          name: selectedDriver.name || '',
          licenseNumber: selectedDriver.licenseNumber || '',
          licenseType: selectedDriver.licenseType || 'LMV',
          licenseExpiryDate: selectedDriver.licenseExpiryDate
            ? new Date(selectedDriver.licenseExpiryDate)
              .toISOString()
              .split('T')[0]
            : '',
          phoneNumber: selectedDriver.phoneNumber || '',
          email: selectedDriver.email || '',
          address: selectedDriver.address || '',
          emergencyContact: selectedDriver.emergencyContact || '',
          emergencyContactPhone: selectedDriver.emergencyContactPhone || '',
          joiningDate: selectedDriver.joiningDate
            ? new Date(selectedDriver.joiningDate).toISOString().split('T')[0]
            : '',
          salary: selectedDriver.salary || 0,
          salaryStatus: selectedDriver.salaryStatus || 'PENDING',
          lastSalaryPaidDate: selectedDriver.lastSalaryPaidDate
            ? new Date(selectedDriver.lastSalaryPaidDate)
              .toISOString()
              .split('T')[0]
            : '',
          nextSalaryDueDate: selectedDriver.nextSalaryDueDate
            ? new Date(selectedDriver.nextSalaryDueDate)
              .toISOString()
              .split('T')[0]
            : '',
          isActive: selectedDriver.isActive ?? true,
          experience: selectedDriver.experience || 0,
          medicalCertificateExpiryDate:
            selectedDriver.medicalCertificateExpiryDate
              ? new Date(selectedDriver.medicalCertificateExpiryDate)
                .toISOString()
                .split('T')[0]
              : '',
          policeVerificationStatus:
            selectedDriver.policeVerificationStatus || 'PENDING',
          assignedVehicleId: selectedDriver.assignedVehicleId || '',
          assignedRouteId: selectedDriver.assignedRouteId || '',
        });
      } else if (isCreateMode) {
        reset(defaultValues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriver, isEditMode, isCreateMode, isOpen, reset]);

  const onSubmitForm = (data: SalaryFormData) => {
    const driverData: TDriver = {
      id: selectedDriver?.id || '',
      name: data.name,
      licenseNumber: data.licenseNumber,
      licenseType: data.licenseType,
      licenseExpiryDate: new Date(data.licenseExpiryDate),
      phoneNumber: data.phoneNumber,
      email: data.email,
      address: data.address,
      emergencyContact: data.emergencyContact,
      emergencyContactPhone: data.emergencyContactPhone,
      joiningDate: new Date(data.joiningDate),
      salary: data.salary,
      salaryStatus: data.salaryStatus,
      lastSalaryPaidDate: data.lastSalaryPaidDate
        ? new Date(data.lastSalaryPaidDate)
        : undefined,
      nextSalaryDueDate: new Date(data.nextSalaryDueDate),
      isActive: data.isActive,
      experience: data.experience,
      medicalCertificateExpiryDate: new Date(data.medicalCertificateExpiryDate),
      policeVerificationStatus: data.policeVerificationStatus,
      assignedVehicleId: data.assignedVehicleId,
      assignedRouteId: data.assignedRouteId,
    };

    if (isEditMode) {
      updateDriver(driverData);
    } else {
      addDriver(driverData);
    }
    setEditMode(undefined);
    setViewType(undefined);
  };

  const handleClose = () => {
    setEditMode(undefined);
    setViewType(undefined);
  };

  const vehicleOptions = vehicles.map((vehicle) => ({
    key: vehicle.id,
    value: vehicle.vehicleNumber,
  }));

  const routeOptions = routes.map((route) => ({
    key: route.id,
    value: route.routeName,
  }));

  if (!isOpen) {
    return null;
  }

  return (
    <div data-testid="add-edit-salary-container">
      <StructuredModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? translateText('administration.routesAndSchedule.editDriver') ||
            'Edit Driver'
            : translateText('administration.routesAndSchedule.addDriver') ||
            'Add Driver'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={false}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="salary-form"
          >
            <Grid
              container
              spacing={3}
              padding={2}
              data-testid="salary-form-container"
            >
              <Grid item xs={12} data-testid="driver-basic-section">
                <Typography variant="h6" gutterBottom>
                  {translateText(
                    'administration.routesAndSchedule.details.basicInfo'
                  ) || 'Basic Information'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} data-testid="driver-name-field">
                <ControlledTextField
                  control={control}
                  name="name"
                  id="driver-name"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.driverName'
                    ) || 'Driver Name'
                  }
                  errorMessage={errors.name?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="driver-phone-field">
                <ControlledTextField
                  control={control}
                  name="phoneNumber"
                  id="driver-phone"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.phoneNumber'
                    ) || 'Phone Number'
                  }
                  errorMessage={errors.phoneNumber?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="driver-email-field">
                <ControlledTextField
                  control={control}
                  name="email"
                  id="driver-email"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.email'
                    ) || 'Email (Optional)'
                  }
                  errorMessage={errors.email?.message}
                  type="email"
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="driver-status-field">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watch('isActive')}
                      onChange={(e) => {
                        methods.setValue('isActive', e.target.checked);
                      }}
                    />
                  }
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.status'
                    ) || 'Active'
                  }
                />
              </Grid>

              <Grid item xs={12} data-testid="driver-license-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText('administration.routesAndSchedule.license') ||
                    'License Information'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                data-testid="driver-license-number-field"
              >
                <ControlledTextField
                  control={control}
                  name="licenseNumber"
                  id="driver-license-number"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.licenseNumber'
                    ) || 'License Number'
                  }
                  errorMessage={errors.licenseNumber?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={4} data-testid="driver-license-type-field">
                <ControlledSelectField
                  control={control}
                  name="licenseType"
                  id="driver-license-type"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.licenseType'
                    ) || 'License Type'
                  }
                  itemList={getLicenseTypeOptions(translateText)}
                  errorMessage={errors.licenseType?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                data-testid="driver-license-expiry-field"
              >
                <ControlledDateField
                  control={control}
                  name="licenseExpiryDate"
                  id="driver-license-expiry"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.licenseExpiry'
                    ) || 'License Expiry Date'
                  }
                  errorMessage={errors.licenseExpiryDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="driver-experience-field">
                <ControlledTextField
                  control={control}
                  name="experience"
                  id="driver-experience"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.experience'
                    ) || 'Experience (years)'
                  }
                  errorMessage={errors.experience?.message}
                  type="number"
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="driver-joining-date-field">
                <ControlledDateField
                  control={control}
                  name="joiningDate"
                  id="driver-joining-date"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.joiningDate'
                    ) || 'Joining Date'
                  }
                  errorMessage={errors.joiningDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="driver-contact-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.routesAndSchedule.details.contact'
                  ) || 'Contact Information'}
                </Typography>
              </Grid>

              <Grid item xs={12} data-testid="driver-address-field">
                <ControlledTextField
                  control={control}
                  name="address"
                  id="driver-address"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.address'
                    ) || 'Address'
                  }
                  errorMessage={errors.address?.message}
                  multiline
                  rows={3}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="driver-emergency-contact-field"
              >
                <ControlledTextField
                  control={control}
                  name="emergencyContact"
                  id="driver-emergency-contact"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.emergencyContact'
                    ) || 'Emergency Contact Name'
                  }
                  errorMessage={errors.emergencyContact?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="driver-emergency-phone-field"
              >
                <ControlledTextField
                  control={control}
                  name="emergencyContactPhone"
                  id="driver-emergency-phone"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.emergencyPhone'
                    ) || 'Emergency Contact Phone'
                  }
                  errorMessage={errors.emergencyContactPhone?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="driver-salary-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.routesAndSchedule.details.salary'
                  ) || 'Salary Information'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4} data-testid="driver-salary-field">
                <ControlledTextField
                  control={control}
                  name="salary"
                  id="driver-salary"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.salary'
                    ) || 'Salary (₹)'
                  }
                  errorMessage={errors.salary?.message}
                  type="number"
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                data-testid="driver-salary-status-field"
              >
                <ControlledSelectField
                  control={control}
                  name="salaryStatus"
                  id="driver-salary-status"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.salaryStatus'
                    ) || 'Salary Status'
                  }
                  itemList={getSalaryStatusOptions(translateText)}
                  errorMessage={errors.salaryStatus?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                data-testid="driver-next-due-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="nextSalaryDueDate"
                  id="driver-next-due-date"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.nextDueDate'
                    ) || 'Next Due Date'
                  }
                  errorMessage={errors.nextSalaryDueDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="driver-last-paid-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="lastSalaryPaidDate"
                  id="driver-last-paid-date"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.lastPaidDate'
                    ) || 'Last Paid Date (Optional)'
                  }
                  errorMessage={errors.lastSalaryPaidDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="driver-certifications-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.routesAndSchedule.details.certifications'
                  ) || 'Certifications & Documents'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="driver-medical-expiry-field"
              >
                <ControlledDateField
                  control={control}
                  name="medicalCertificateExpiryDate"
                  id="driver-medical-expiry"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.medicalExpiry'
                    ) || 'Medical Certificate Expiry Date'
                  }
                  errorMessage={errors.medicalCertificateExpiryDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="driver-police-verification-field"
              >
                <ControlledSelectField
                  control={control}
                  name="policeVerificationStatus"
                  id="driver-police-verification"
                  label={
                    translateText(
                      'administration.routesAndSchedule.details.policeVerification'
                    ) || 'Police Verification Status'
                  }
                  itemList={getPoliceVerificationOptions(translateText)}
                  errorMessage={errors.policeVerificationStatus?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="driver-assignments-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.routesAndSchedule.details.assignments'
                  ) || 'Assignments'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} data-testid="driver-vehicle-field">
                <ControlledSelectField
                  control={control}
                  name="assignedVehicleId"
                  id="driver-vehicle"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.vehicle'
                    ) || 'Assigned Vehicle (Optional)'
                  }
                  itemList={vehicleOptions}
                  errorMessage={errors.assignedVehicleId?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="driver-route-field">
                <ControlledSelectField
                  control={control}
                  name="assignedRouteId"
                  id="driver-route"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.route'
                    ) || 'Assigned Route (Optional)'
                  }
                  itemList={routeOptions}
                  errorMessage={errors.assignedRouteId?.message}
                  isGridItem={false}
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditSalaryDetailsView;
